// ============================================
// 镜界选址对赌 v4.0 - LinkWave 统一风格
// 集成全屏地图 | 统一布局
// ============================================

import React, { useState } from 'react';
import { 
  MapPin, Search, Crosshair, Zap, Shield, TrendingUp, 
  Users, Store, DollarSign,
  CheckCircle2, RotateCw,
  Brain, Target,
  Download, Share2,
  Building2,
  Loader2, Clock, History
} from 'lucide-react';
import { PageHeader, TabNav, PageContainer, CardGrid, ContentCard, StatCard, ActionButton } from './LayoutComponents';
import AMapComponent from './AMapComponent';

// ==================== 类型定义 ====================

interface AnalysisResult {
  score: number;
  verdict: 'strongly_recommend' | 'recommend' | 'neutral' | 'not_recommend';
  trafficScore: number;
  competitionScore: number;
  populationScore: number;
  facilityScore: number;
  rentEstimate: { min: number; max: number; unit: string };
  nearbyCompetitors: CompetitorInfo[];
  nearbyPOIs: POIInfo[];
  demographics: {
    targetPopulation: number;
    ageDistribution: { label: string; value: number }[];
    consumptionLevel: 'high' | 'medium' | 'low';
  };
  aiInsights: string[];
  riskFactors: string[];
  monthlyRevenueEstimate: { min: number; max: number };
  paybackPeriod: number;
}

interface CompetitorInfo {
  name: string;
  distance: number;
  rating: number;
  type: 'direct' | 'indirect';
}

interface POIInfo {
  type: string;
  count: number;
  distance: number;
  icon: string;
}

interface AnalysisTask {
  id: string;
  address: string;
  status: 'input' | 'analyzing' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  result?: AnalysisResult;
  createdAt: Date;
  selectedPoint?: { lng: number; lat: number };
}

// ==================== 模拟数据 ====================

const MOCK_ANALYSIS_RESULT: AnalysisResult = {
  score: 87,
  verdict: 'strongly_recommend',
  trafficScore: 85,
  competitionScore: 78,
  populationScore: 92,
  facilityScore: 88,
  rentEstimate: { min: 18000, max: 25000, unit: '元/月' },
  nearbyCompetitors: [
    { name: '宝岛眼镜', distance: 850, rating: 4.5, type: 'direct' },
    { name: '大明眼镜', distance: 1200, rating: 4.2, type: 'direct' },
    { name: '眼科医院视光中心', distance: 2000, rating: 4.8, type: 'indirect' },
  ],
  nearbyPOIs: [
    { type: '中小学', count: 3, distance: 500, icon: '🏫' },
    { type: '写字楼', count: 8, distance: 300, icon: '🏢' },
    { type: '商场', count: 2, distance: 800, icon: '🏬' },
    { type: '社区', count: 12, distance: 600, icon: '🏘️' },
    { type: '地铁站', count: 1, distance: 400, icon: '🚇' },
  ],
  demographics: {
    targetPopulation: 45000,
    ageDistribution: [
      { label: '儿童(6-12岁)', value: 18 },
      { label: '青少年(13-18岁)', value: 22 },
      { label: '青年(19-35岁)', value: 35 },
      { label: '中年(36-55岁)', value: 20 },
      { label: '老年(55岁+)', value: 5 },
    ],
    consumptionLevel: 'high',
  },
  aiInsights: [
    '该区域3公里内目标客群密度高，特别是青少年和青年群体占比达75%',
    '周边教育资源丰富，3所中小学可带来稳定的青少年配镜需求',
    '竞争对手分布相对分散，最近竞品距离850米，市场空间充足',
    '地铁口400米距离带来稳定客流，上班族通勤必经之地',
    '商圈成熟度高，配套设施完善，客户消费意愿强',
  ],
  riskFactors: [
    '周边已有2家连锁眼镜店，需注意差异化定位',
    '租金水平偏高，需控制初始投资规模',
  ],
  monthlyRevenueEstimate: { min: 120000, max: 180000 },
  paybackPeriod: 14,
};

const ANALYSIS_STEPS = [
  { id: 'geo', label: '地理信息解析', duration: 1500 },
  { id: 'poi', label: '周边POI数据采集', duration: 2000 },
  { id: 'competitor', label: '竞争态势分析', duration: 1800 },
  { id: 'demographics', label: '人口画像构建', duration: 2200 },
  { id: 'traffic', label: '人流热力模拟', duration: 2500 },
  { id: 'scoring', label: '智能评分计算', duration: 1500 },
  { id: 'prediction', label: '营收预测模型', duration: 2000 },
  { id: 'report', label: '生成分析报告', duration: 1000 },
];

// ==================== 主组件 ====================

export const LocationScoutModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [currentTask, setCurrentTask] = useState<AnalysisTask | null>(null);
  const [tasks, setTasks] = useState<AnalysisTask[]>([]);

  const startAnalysis = (address: string, point?: { lng: number; lat: number }) => {
    const newTask: AnalysisTask = {
      id: `LOC_${Date.now()}`,
      address,
      status: 'analyzing',
      progress: 0,
      currentStep: '准备开始分析...',
      createdAt: new Date(),
      selectedPoint: point,
    };
    setCurrentTask(newTask);
    setTasks([newTask, ...tasks]);
    simulateAnalysis(newTask);
  };

  const simulateAnalysis = (_task: AnalysisTask) => {
    let stepIndex = 0;
    const totalSteps = ANALYSIS_STEPS.length;

    const runStep = () => {
      if (stepIndex >= totalSteps) {
        setCurrentTask(prev => prev ? {
          ...prev,
          status: 'completed',
          progress: 100,
          result: MOCK_ANALYSIS_RESULT,
        } : null);
        return;
      }

      const step = ANALYSIS_STEPS[stepIndex];
      setCurrentTask(prev => prev ? {
        ...prev,
        currentStep: step.label,
        progress: Math.round((stepIndex / totalSteps) * 100),
      } : null);

      stepIndex++;
      setTimeout(runStep, step.duration);
    };

    runStep();
  };

  return (
    <PageContainer>
      {/* 头部 */}
      <PageHeader
        icon={Target}
        title="选址对赌"
        subtitle="AI驱动的智能选址决策系统"
        extra={
          <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-[rgba(196,30,58,0.1)] to-[rgba(139,71,137,0.1)] rounded-xl border border-[rgba(196,30,58,0.2)]">
            <Shield className="w-5 h-5 text-[#C41E3A]" />
            <span className="text-sm font-medium text-[#C41E3A]">6个月内倒闭全额退款</span>
          </div>
        }
      />

      {/* Tab 导航 */}
      <TabNav
        tabs={[
          { id: 'new', label: '开始选址', icon: MapPin },
          { id: 'history', label: '历史分析', icon: History, badge: tasks.length },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* 内容区域 */}
      {activeTab === 'new' ? (
        currentTask?.status === 'analyzing' ? (
          <AnalyzingView task={currentTask} />
        ) : currentTask?.status === 'completed' && currentTask.result ? (
          <AnalysisResultView 
            result={currentTask.result} 
            address={currentTask.address}
            selectedPoint={currentTask.selectedPoint}
            onReset={() => setCurrentTask(null)}
          />
        ) : (
          <NewAnalysisView onStart={startAnalysis} />
        )
      ) : (
        <HistoryView tasks={tasks} />
      )}
    </PageContainer>
  );
};

// ==================== 子组件 ====================

// 1. 新建分析 - 地图选点界面
const NewAnalysisView: React.FC<{ 
  onStart: (address: string, point?: { lng: number; lat: number }) => void 
}> = ({ onStart }) => {
  const [inputAddress, setInputAddress] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<{ lng: number; lat: number } | null>(null);

  const handleLocate = () => {
    setIsLocating(true);
    setTimeout(() => {
      setIsLocating(false);
      const mockAddress = '北京市朝阳区建国路88号SOHO现代城';
      const mockPoint = { lng: 116.481488, lat: 39.990464 };
      setInputAddress(mockAddress);
      setSelectedPoint(mockPoint);
    }, 1500);
  };

  const handleMapClick = (lng: number, lat: number, address?: string) => {
    setSelectedPoint({ lng, lat });
    if (address) {
      setInputAddress(address);
    }
  };

  const handleStartAnalysis = () => {
    if (inputAddress) {
      onStart(inputAddress, selectedPoint || undefined);
    }
  };

  return (
    <div className="space-y-6">
      {/* 服务承诺卡片 */}
      <CardGrid cols={4}>
        <StatCard
          icon={Shield}
          label="倒闭全额退款"
          value="6个月"
          color="red"
          highlight
        />
        <StatCard
          icon={CheckCircle2}
          label="敢对赌标准"
          value="87+分"
          color="green"
          highlight
        />
        <StatCard
          icon={Brain}
          label="AI预测准确率"
          value="92%"
          color="purple"
          highlight
        />
        <StatCard
          icon={TrendingUp}
          label="对赌赔付次数"
          value="0次"
          color="green"
        />
      </CardGrid>

      {/* 地图选点区域 */}
      <ContentCard title="选择店铺位置" extra={<span className="text-sm text-gray-500">点击地图选点或输入地址</span>}>
        <div className="flex gap-6">
          {/* 左侧控制面板 */}
          <div className="w-80 space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">店铺地址</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={inputAddress}
                  onChange={(e) => setInputAddress(e.target.value)}
                  placeholder="请输入详细地址"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#C41E3A] focus:ring-2 focus:ring-[rgba(196,30,58,0.1)] transition-all"
                />
              </div>
            </div>

            <ActionButton 
              icon={isLocating ? undefined : Crosshair} 
              variant="secondary" 
              onClick={handleLocate}
            >
              {isLocating ? <><RotateCw className="w-4 h-4 animate-spin" /> 定位中...</> : '使用当前位置'}
            </ActionButton>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">热门商圈类型</label>
              <div className="grid grid-cols-2 gap-2">
                {['市中心商圈', '大学城', '高铁站', '大型社区', '写字楼聚集区', '商业街'].map((area) => (
                  <button
                    key={area}
                    onClick={() => setInputAddress(area)}
                    className="px-3 py-2 text-sm text-gray-600 bg-gray-50 hover:bg-[rgba(196,30,58,0.05)] hover:text-[#C41E3A] rounded-lg transition-colors text-left"
                  >
                    {area}
                  </button>
                ))}
              </div>
            </div>

            {selectedPoint && (
              <div className="bg-[rgba(196,30,58,0.05)] rounded-xl p-4 border border-[rgba(196,30,58,0.1)]">
                <div className="flex items-center gap-2 text-[#C41E3A] mb-2">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">已选位置</span>
                </div>
                <p className="text-sm text-gray-600">
                  经度: {selectedPoint.lng.toFixed(6)}<br />
                  纬度: {selectedPoint.lat.toFixed(6)}
                </p>
              </div>
            )}

            <ActionButton 
              icon={Zap} 
              variant="primary" 
              onClick={handleStartAnalysis}
            >
              启动AI选址分析
            </ActionButton>

            <p className="text-xs text-gray-400 text-center">
              分析过程约需15秒，AI将采集8个维度数据
            </p>
          </div>

          {/* 地图区域 - 支持全屏 */}
          <div className="flex-1">
            <AMapComponent
              height="500px"
              center={[116.397428, 39.90923]}
              zoom={12}
              enableClickToSelect
              onMapClick={handleMapClick}
              selectedPoint={selectedPoint}
              markers={[
                { position: [116.47, 39.91], title: '宝岛眼镜', content: '距离 850m', color: 'bg-red-500', icon: '竞' },
                { position: [116.31, 39.98], title: '大明眼镜', content: '距离 1.2km', color: 'bg-red-500', icon: '竞' },
              ]}
              allowFullscreen
            />
          </div>
        </div>
      </ContentCard>

      {/* 分析维度说明 */}
      <CardGrid cols={4}>
        {[
          { icon: Users, label: '人流量分析', desc: '周边1.5km人流密度' },
          { icon: Store, label: '竞争分析', desc: '竞品分布与距离' },
          { icon: Building2, label: '配套设施', desc: '教育/商业/交通' },
          { icon: DollarSign, label: '租金评估', desc: '市场行情预测' },
        ].map((item) => (
          <ContentCard key={item.label} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[rgba(196,30,58,0.1)] rounded-lg flex items-center justify-center flex-shrink-0">
              <item.icon className="w-5 h-5 text-[#C41E3A]" />
            </div>
            <div>
              <p className="font-medium text-gray-900 text-sm">{item.label}</p>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          </ContentCard>
        ))}
      </CardGrid>
    </div>
  );
};

// 2. 分析中 - 努力干活的动画
const AnalyzingView: React.FC<{ task: AnalysisTask }> = ({ task }) => (
  <ContentCard className="py-12">
    <div className="max-w-xl mx-auto text-center">
      <div className="relative w-32 h-32 mx-auto mb-8">
        <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-transparent border-t-[#C41E3A] rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
        <div className="absolute inset-4 bg-[rgba(139,71,137,0.1)] rounded-full animate-pulse"></div>
        <div className="absolute inset-6 gradient-brand rounded-full flex items-center justify-center">
          <Brain className="w-10 h-10 text-white animate-pulse" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">AI正在努力分析中...</h2>
      <p className="text-gray-500 mb-8">{task.address}</p>

      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">分析进度</span>
          <span className="font-bold text-[#C41E3A]">{task.progress}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full gradient-brand rounded-full transition-all duration-500"
            style={{ width: `${task.progress}%` }}
          ></div>
        </div>
      </div>

      <div className="inline-flex items-center gap-3 px-6 py-3 bg-[rgba(196,30,58,0.05)] rounded-2xl">
        <Loader2 className="w-5 h-5 text-[#C41E3A] animate-spin" />
        <span className="text-[#C41E3A] font-medium">{task.currentStep}</span>
      </div>

      <div className="mt-8 grid grid-cols-4 gap-4">
        {[
          { label: '已采集POI', value: Math.floor(task.progress * 1.2), unit: '个' },
          { label: '竞品分析', value: Math.floor(task.progress * 0.08), unit: '家' },
          { label: '数据源', value: 4, unit: '个' },
          { label: '计算节点', value: 12, unit: '个' },
        ].map((item) => (
          <div key={item.label} className="text-center p-3 bg-gray-50 rounded-xl">
            <p className="text-xl font-bold text-gray-900">{item.value}{item.unit}</p>
            <p className="text-xs text-gray-500">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  </ContentCard>
);

// 3. 分析结果 - 成就感展示
const AnalysisResultView: React.FC<{ 
  result: AnalysisResult; 
  address: string;
  selectedPoint?: { lng: number; lat: number };
  onReset: () => void;
}> = ({ result, address, selectedPoint, onReset }) => {
  const getVerdictInfo = () => {
    switch (result.verdict) {
      case 'strongly_recommend': return { text: '强烈推荐', color: 'green', bg: 'bg-[rgba(34,139,34,0.1)]', textColor: 'text-[#228B22]' };
      case 'recommend': return { text: '推荐', color: 'red', bg: 'bg-[rgba(196,30,58,0.1)]', textColor: 'text-[#C41E3A]' };
      case 'neutral': return { text: '谨慎考虑', color: 'default', bg: 'bg-gray-100', textColor: 'text-gray-600' };
      default: return { text: '不推荐', color: 'default', bg: 'bg-red-50', textColor: 'text-red-600' };
    }
  };

  const verdict = getVerdictInfo();

  return (
    <div className="space-y-6">
      {/* 顶部结果卡片 */}
      <div className="gradient-brand rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${verdict.bg} ${verdict.textColor}`}>
                  {verdict.text}
                </span>
                {result.score >= 87 && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-bold">
                    <Shield className="w-4 h-4" />
                    可对赌
                  </span>
                )}
              </div>
              <h2 className="text-3xl font-bold mb-2">{address}</h2>
              <p className="text-white/80">AI选址分析报告</p>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-white rounded-2xl flex flex-col items-center justify-center shadow-2xl">
                <span className="text-5xl font-bold text-[#C41E3A]">{result.score}</span>
                <span className="text-sm text-gray-500 mt-1">综合评分</span>
              </div>
            </div>
          </div>

          <CardGrid cols={4} className="mt-8">
            {[
              { icon: Users, label: '人流评分', score: result.trafficScore },
              { icon: Store, label: '竞争评分', score: result.competitionScore },
              { icon: Building2, label: '配套评分', score: result.facilityScore },
              { icon: Target, label: '客群评分', score: result.populationScore },
            ].map((item) => (
              <div key={item.label} className="bg-white/20 rounded-xl p-4 backdrop-blur">
                <div className="flex items-center gap-2 mb-1">
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                </div>
                <p className="text-2xl font-bold">{item.score}</p>
              </div>
            ))}
          </CardGrid>
        </div>
      </div>

      {/* 地图展示 */}
      {selectedPoint && (
        <ContentCard title="选址位置">
          <AMapComponent
            height="300px"
            center={[selectedPoint.lng, selectedPoint.lat]}
            zoom={15}
            selectedPoint={selectedPoint}
            markers={result.nearbyCompetitors.map((comp) => ({
              position: [selectedPoint.lng + (Math.random() - 0.5) * 0.02, selectedPoint.lat + (Math.random() - 0.5) * 0.02],
              title: comp.name,
              content: `距离 ${comp.distance}m · ${comp.rating}分`,
              color: 'bg-red-500',
              icon: '竞',
            }))}
            allowFullscreen
          />
        </ContentCard>
      )}

      {/* 主要内容区 */}
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2 space-y-5">
          <ContentCard title="AI核心洞察">
            <div className="space-y-3">
              {result.aiInsights.map((insight, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-6 h-6 bg-[rgba(34,139,34,0.1)] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-[#228B22]" />
                  </div>
                  <p className="text-gray-700">{insight}</p>
                </div>
              ))}
            </div>
          </ContentCard>

          <ContentCard title="周边配套分析">
            <div className="grid grid-cols-5 gap-3">
              {result.nearbyPOIs.map((poi) => (
                <div key={poi.type} className="text-center p-4 bg-gray-50 rounded-xl">
                  <span className="text-3xl">{poi.icon}</span>
                  <p className="font-bold text-gray-900 mt-2">{poi.count}</p>
                  <p className="text-xs text-gray-500">{poi.type}</p>
                  <p className="text-xs text-gray-400">{poi.distance}m</p>
                </div>
              ))}
            </div>
          </ContentCard>

          <ContentCard title="竞争态势">
            <div className="space-y-3">
              {result.nearbyCompetitors.map((comp, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Store className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{comp.name}</p>
                      <p className="text-sm text-gray-500">{comp.rating}分</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{comp.distance}m</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${comp.type === 'direct' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                      {comp.type === 'direct' ? '直接竞争' : '间接竞争'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ContentCard>
        </div>

        <div className="space-y-5">
          <ContentCard className="bg-gradient-to-br from-[#228B22] to-[#20B2AA] text-white">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              AI营收预测
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-white/80">预估月营业额</p>
                <p className="text-3xl font-bold">
                  ¥{(result.monthlyRevenueEstimate.min / 10000).toFixed(0)}-{(result.monthlyRevenueEstimate.max / 10000).toFixed(0)}万
                </p>
              </div>
              <div>
                <p className="text-sm text-white/80">预计回本周期</p>
                <p className="text-2xl font-bold">{result.paybackPeriod}个月</p>
              </div>
              <div>
                <p className="text-sm text-white/80">租金预估</p>
                <p className="text-lg">
                  ¥{result.rentEstimate.min.toLocaleString()}-{result.rentEstimate.max.toLocaleString()}/月
                </p>
              </div>
            </div>
          </ContentCard>

          <ContentCard title="目标客群画像">
            <div className="text-center mb-4">
              <p className="text-3xl font-bold text-gray-900">{result.demographics.targetPopulation.toLocaleString()}</p>
              <p className="text-sm text-gray-500">目标人群总数</p>
            </div>
            <div className="space-y-2">
              {result.demographics.ageDistribution.map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-medium">{item.value}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#C41E3A] rounded-full" style={{ width: `${item.value}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </ContentCard>

          <div className="space-y-3">
            {result.score >= 87 && (
              <ActionButton variant="primary">
                签约对赌服务
              </ActionButton>
            )}
            <div className="flex gap-3">
              <ActionButton icon={Download} variant="secondary" className="flex-1">
                导出
              </ActionButton>
              <ActionButton icon={Share2} variant="secondary" className="flex-1">
                分享
              </ActionButton>
            </div>
            <ActionButton variant="ghost" onClick={onReset}>
              分析新地址
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. 历史记录
const HistoryView: React.FC<{ tasks: AnalysisTask[] }> = ({ tasks }) => (
  <ContentCard title="历史分析记录">
    {tasks.length === 0 ? (
      <div className="py-16 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500">暂无历史记录</p>
      </div>
    ) : (
      <div className="space-y-3">
        {tasks.map((task) => (
          <div key={task.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                task.status === 'completed' ? 'bg-[rgba(34,139,34,0.1)]' :
                task.status === 'analyzing' ? 'bg-blue-50' :
                'bg-gray-100'
              }`}>
                {task.status === 'completed' ? (
                  <CheckCircle2 className="w-6 h-6 text-[#228B22]" />
                ) : task.status === 'analyzing' ? (
                  <Loader2 className="w-6 h-6 text-[#C41E3A] animate-spin" />
                ) : (
                  <Clock className="w-6 h-6 text-gray-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">{task.address}</p>
                <p className="text-sm text-gray-500">
                  {task.createdAt.toLocaleString('zh-CN')}
                </p>
              </div>
            </div>
            <div className="text-right">
              {task.status === 'completed' && task.result ? (
                <div>
                  <p className="text-2xl font-bold text-gray-900">{task.result.score}分</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    task.result.score >= 87 ? 'bg-[rgba(34,139,34,0.1)] text-[#228B22]' :
                    task.result.score >= 70 ? 'bg-blue-50 text-blue-700' :
                    'bg-red-50 text-red-700'
                  }`}>
                    {task.result.score >= 87 ? '可对赌' :
                     task.result.score >= 70 ? '推荐' : '谨慎'}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-gray-500">
                  {task.status === 'analyzing' ? '分析中...' : '等待中'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </ContentCard>
);

export default LocationScoutModule;
